import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { createOrder, updateOrderPayment } from "../services/order.service";
import { getPaymentSettings, uploadPaymentScreenshot } from "../services/payment.service";
import { getCouponByCode, incrementCouponUsage } from "../services/coupon.service";
import QRCode from "react-qr-code";

const PaymentSection = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { cartSubtotal, totalShipping, cartTotal, clearCart, taxCalculation } = useCart();
    
    const [paymentMethod, setPaymentMethod] = useState("qr");
    const [paymentSettings, setPaymentSettings] = useState(null);
    const [screenshot, setScreenshot] = useState(null);
    const [transactionId, setTransactionId] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [errors, setErrors] = useState({});
    const [showFullQR, setShowFullQR] = useState(false);
    
    // Coupon States
    const [couponCode, setCouponCode] = useState("");
    const [appliedCoupon, setAppliedCoupon] = useState(null);
    const [couponError, setCouponError] = useState("");
    const [couponLoading, setCouponLoading] = useState(false);

    const orderDataFromCheckout = location.state?.orderData;

    useEffect(() => {
        if (!orderDataFromCheckout) {
            navigate("/checkout");
            return;
        }
        
        const fetchSettings = async () => {
            const settings = await getPaymentSettings();
            setPaymentSettings(settings);
        };
        fetchSettings();
    }, [orderDataFromCheckout, navigate]);

    const handleFileChange = (e) => {
        const file = e.target.files[0] || null;
        setScreenshot(file);
        if (file) {
            setErrors(prev => ({ ...prev, screenshot: "" }));
        }
    };

    const handleApplyCoupon = async () => {
        setCouponError("");
        if (!couponCode.trim()) return;
        
        setCouponLoading(true);
        const coupon = await getCouponByCode(couponCode.trim());
        setCouponLoading(false);
        
        if (!coupon) {
            setCouponError("Invalid coupon code.");
            return;
        }
        
        if (!coupon.isActive) {
            setCouponError("This coupon is no longer active.");
            return;
        }
        
        if (coupon.expiresAt && new Date(coupon.expiresAt) < new Date()) {
            setCouponError("This coupon has expired.");
            return;
        }
        
        if (coupon.maxUses && coupon.usedCount >= coupon.maxUses) {
            setCouponError("This coupon has reached its usage limit.");
            return;
        }
        
        if (coupon.minOrderAmount && cartSubtotal < coupon.minOrderAmount) {
            setCouponError(`Minimum order amount of Rs.${coupon.minOrderAmount} required.`);
            return;
        }
        
        setAppliedCoupon(coupon);
    };

    const handleRemoveCoupon = () => {
        setAppliedCoupon(null);
        setCouponCode("");
    };

    const hasExclusiveTax = taxCalculation?.exclusive > 0;

    let discountAmount = 0;
    if (appliedCoupon) {
        if (appliedCoupon.type === 'PERCENT') {
            discountAmount = cartSubtotal * (appliedCoupon.value / 100);
        } else if (appliedCoupon.type === 'FIXED') {
            discountAmount = appliedCoupon.value;
        }
    }
    
    const finalTotal = Math.max(1, cartTotal - discountAmount);

    const upiId = paymentSettings?.upiId;
    const merchantName = paymentSettings?.merchantName;
    const upiString = `upi://pay?pa=${upiId}&pn=${encodeURIComponent(merchantName)}&am=${finalTotal.toFixed(2)}&cu=INR`;

    const handlePlaceOrder = async () => {
        setError("");
        setLoading(true);

        try {
            if (paymentMethod === "qr") {
                const newErrors = {};
                if (!screenshot) newErrors.screenshot = "Payment screenshot is required";
                if (!transactionId.trim()) newErrors.transactionId = "Transaction ID is required";

                if (Object.keys(newErrors).length > 0) {
                    setErrors(newErrors);
                    setLoading(false);
                    return;
                }
            }
            setErrors({});

            // 1. Create the base order
            const fullOrderData = {
                ...orderDataFromCheckout,
                paymentMethod: "QR_PAYMENT",
                paymentStatus: "PENDING",
                status: "PENDING",
                total: finalTotal,
                discountAmount: discountAmount,
                couponCode: appliedCoupon ? appliedCoupon.code : null,
                couponId: appliedCoupon ? appliedCoupon.id : null
            };

            const orderId = await createOrder(fullOrderData);

            // 2. If QR Payment, upload screenshot and update order
            if (paymentMethod === "qr") {
                const screenshotUrl = await uploadPaymentScreenshot(orderId, screenshot);
                await updateOrderPayment(orderId, {
                    paymentStatus: "AWAITING_VERIFICATION",
                    transactionId: transactionId,
                    paymentScreenshot: screenshotUrl
                });
            }

            if (appliedCoupon) {
                await incrementCouponUsage(appliedCoupon.id);
            }

            // 3. Success
            clearCart();
            navigate("/orders", { state: { success: true, orderId } });
        } catch (err) {
            console.error("Order error:", err);
            setError("Failed to place order. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <section className="checkout py-80">
                <div className="container container-lg">
                    <div className="row gy-4">
                        <div className="col-xl-8">
                            <div className="checkout-box bg-white p-32 rounded-16 border border-gray-100 shadow-sm">
                                <h5 className="mb-24">Choose Payment Method</h5>
                                
                                <div className="payment-methods space-y-16">
                                    {/* QR Payment Option */}
                                    <div 
                                        className={`p-20 rounded-12 border-2 transition-all cursor-pointer ${paymentMethod === 'qr' ? 'border-main-600 bg-main-50' : 'border-gray-100 hover:border-gray-200'}`}
                                        onClick={() => setPaymentMethod('qr')}
                                    >
                                        <div className="flex-between">
                                            <div className="flex-align gap-12">
                                                <div className={`w-20 h-20 rounded-circle border-2 flex-center ${paymentMethod === 'qr' ? 'border-main-600' : 'border-gray-300'}`}>
                                                    {paymentMethod === 'qr' && <div className="w-10 h-10 rounded-circle bg-main-600"></div>}
                                                </div>
                                                <span className="fw-bold">Prepaid (GPay, PhonePe, Paytm / UPI / QR)</span>
                                            </div>
                                            <i className="ph ph-qr-code text-2xl text-gray-400"></i>
                                        </div>
                                        
                                        {paymentMethod === 'qr' && (
                                            <div className="mt-20 pt-20 border-top border-gray-200">
                                                {paymentSettings ? (
                                                    <div className="text-center mb-24">
                                                        <p className="mb-12 text-sm fw-medium text-gray-700">Scan to pay Rs.{finalTotal.toFixed(0)}</p>
                                                        <div 
                                                            className="position-relative d-inline-block cursor-pointer group bg-white p-16 rounded-12 border border-gray-200 transition-all hover:brightness-90"
                                                            onClick={() => setShowFullQR(true)}
                                                        >
                                                            <div className="position-relative d-inline-block">
                                                                <QRCode 
                                                                    value={upiString} 
                                                                    size={200}
                                                                    level="H"
                                                                    style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                                                                    viewBox={`0 0 256 256`}
                                                                />
                                                                <div className="position-absolute top-50 start-50 translate-middle bg-white p-4 rounded-circle shadow-sm d-flex align-items-center justify-content-center" style={{ width: '56px', height: '56px' }}>
                                                                    <img src="/assets/images/logo/logo2.png" alt="Logo" className="w-100 h-100 object-fit-contain rounded-circle" />
                                                                </div>
                                                            </div>
                                                            <div className="mt-12 pt-12 border-top border-gray-100 text-start ps-8">
                                                                <p className="text-sm fw-bold text-gray-800 mb-8" style={{ wordBreak: 'break-all' }}>
                                                                    <span className="text-xs text-gray-500 uppercase tracking-wider me-8">UPI ID :</span> 
                                                                    {upiId}
                                                                </p>
                                                                <p className="text-sm fw-bold text-gray-800 mb-0">
                                                                    <span className="text-xs text-gray-500 uppercase tracking-wider me-8">UPI NO :</span> 
                                                                    {paymentSettings?.gpayNumber}
                                                                </p>
                                                            </div>
                                                            <div className="position-absolute top-50 start-50 translate-middle bg-main-600 text-white p-8 rounded-circle opacity-0 group-hover-opacity-100 transition-all shadow-lg" style={{ zIndex: 10 }}>
                                                                <i className="ph ph-magnifying-glass-plus fw-bold"></i>
                                                            </div>
                                                            <p className="mt-12 text-xs text-main-600 fw-bold text-center">Click to enlarge</p>
                                                        </div>
                                                        <p className="mt-8 text-sm fw-bold text-gray-800 mb-0">{merchantName}</p>
                                                    </div>
                                                ) : (
                                                    <div className="alert alert-warning text-xs mb-24">Payment settings loading...</div>
                                                )}

                                                <div className="space-y-16">
                                                    <div className="form-group">
                                                        <label className="text-sm fw-bold mb-8">Transaction ID / UTR <span className="text-danger-600">*</span></label>
                                                        <input 
                                                            type="text" 
                                                            className={`common-input rounded-12 ${errors.transactionId ? 'border-danger-600' : ''}`} 
                                                            placeholder="Enter 12-digit transaction ID"
                                                            maxLength="12"
                                                            value={transactionId}
                                                            onChange={(e) => {
                                                                setTransactionId(e.target.value);
                                                                if (e.target.value.length === 12) setErrors(prev => ({...prev, transactionId: ''}));
                                                            }}
                                                        />
                                                        {errors.transactionId && <p className="text-danger-600 text-xs mt-4">{errors.transactionId}</p>}
                                                    </div>
                                                    <div className="form-group">
                                                        <label className="text-sm fw-bold mb-8">Payment Screenshot <span className="text-danger-600">*</span></label>
                                                        <input 
                                                            type="file" 
                                                            className={`common-input rounded-12 pt-10 ${errors.screenshot ? 'border-danger-600' : ''}`} 
                                                            accept="image/*"
                                                            onChange={handleFileChange}
                                                        />
                                                        {errors.screenshot && <p className="text-danger-600 text-xs mt-4">{errors.screenshot}</p>}
                                                    </div>
                                                    
                                                    <div className="p-12 bg-main-50 rounded-8 border border-main-100 mt-16">
                                                        <p className="text-xs text-main-700 mb-0">
                                                            <i className="ph ph-info fw-bold"></i> Your order will be processed once our team verifies your payment. This usually takes 1-2 hours.
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {error && (
                                    <div className="mt-24 p-16 rounded-12 bg-danger-50 text-danger-600 text-sm fw-medium">
                                        {error}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="col-xl-4">
                            <div className="bg-white p-32 rounded-16 border border-gray-100 shadow-sm" style={{ position: 'sticky', top: '220px', zIndex: 5, marginTop: '40px' }}>
                                <h6 className="text-xl mb-24 fw-bold">Order Summary</h6>
                                <div className="bg-gray-50 rounded-12 p-20 mb-24">


                                    <div className="flex-between mb-12">
                                        <span className="text-gray-600">Subtotal</span>
                                        <span className="fw-bold">Rs.{cartSubtotal.toFixed(0)}</span>
                                    </div>
                                    {hasExclusiveTax && (
                                        <div className="flex-between mb-12">
                                            <span className="text-gray-600">GST</span>
                                            <span className="fw-bold">Rs.{taxCalculation.exclusive.toFixed(0)}</span>
                                        </div>
                                    )}
                                    {appliedCoupon && (
                                        <div className="flex-between mb-12 text-success-600">
                                            <span>Discount ({appliedCoupon.code})</span>
                                            <span className="fw-bold">-Rs.{discountAmount.toFixed(0)}</span>
                                        </div>
                                    )}
                                    <div className="flex-between mb-12">
                                        <span className="text-gray-600">Shipping</span>
                                        <span className={totalShipping === 0 ? "text-success-600 fw-bold" : "fw-bold"}>
                                            {totalShipping === 0 ? "FREE" : `Rs.${totalShipping.toFixed(0)}`}
                                        </span>
                                    </div>
                                    <div className="flex-between pt-12 border-top border-gray-200 mt-12">
                                        <span className="text-lg fw-bold">Total</span>
                                        <span className="text-xl fw-bold text-main-600">Rs.{finalTotal.toFixed(0)}</span>
                                    </div>

                                    {/* Coupon Input */}
                                    <div className="mt-16 pt-16">
                                        <div className="d-flex align-items-center gap-8 w-100">
                                            <input 
                                                type="text" 
                                                className="common-input rounded-8 py-8 px-12 bg-white flex-grow-1 border border-gray-500" 
                                                placeholder="Enter coupon code" 
                                                style={{ minWidth: 0 }}
                                                value={couponCode}
                                                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                                                disabled={appliedCoupon !== null}
                                            />
                                            {appliedCoupon ? (
                                                <button 
                                                    onClick={handleRemoveCoupon}
                                                    className="px-16 py-8 rounded-8 fw-bold text-sm flex-shrink-0 text-nowrap"
                                                >
                                                    Remove
                                                </button>
                                            ) : (
                                                <button 
                                                    onClick={handleApplyCoupon}
                                                    disabled={couponLoading || !couponCode.trim()}
                                                    className="px-16 py-8 rounded-8 fw-bold text-sm flex-shrink-0 text-nowrap border border-grey-500"
                                                    style={{ 
                                                        backgroundColor: 'var(--brand-primary)', 
                                                        color: 'white',
                                                        cursor: loading || (paymentMethod === "qr" && (!screenshot || transactionId.trim().length !== 12)) ? 'not-allowed' : 'pointer'
                                                    }}                                                
                                                >
                                                    {couponLoading ? "..." : "Apply"}
                                                </button>
                                            )}
                                        </div>
                                        {couponError && <p className="text-danger-600 text-xs mt-4">{couponError}</p>}
                                        {appliedCoupon && <p className="text-success-600 text-xs mt-4">Coupon applied successfully!</p>}
                                    </div>
                                </div>

                                <div className="mt-32">
                                    <button 
                                        onClick={handlePlaceOrder}
                                        disabled={loading || (paymentMethod === "qr" && (!screenshot || transactionId.trim().length !== 12))}
                                        className={`btn btn-main-600 w-100 rounded-12 py-18 fw-bold text-lg flex-center gap-12 shadow-sm transition-2 ${loading || (paymentMethod === "qr" && (!screenshot || transactionId.trim().length !== 12)) ? 'opacity-50 grayscale' : 'hover-bg-main-700'}`}
                                        style={{ 
                                            backgroundColor: 'var(--brand-primary)', 
                                            color: 'white',
                                            cursor: loading || (paymentMethod === "qr" && (!screenshot || transactionId.trim().length !== 12)) ? 'not-allowed' : 'pointer'
                                        }}
                                    >
                                        {loading ? (
                                            <><div className="spinner-border spinner-border-sm"></div> Processing...</>
                                        ) : (
                                            <><i className="ph ph-check-circle"></i> Complete Order</>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Full QR Modal Overlay - Using Portal to escape stacking context */}
            {showFullQR && createPortal(
                <div 
                    className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center transition-all"
                    style={{ backgroundColor: 'rgba(0,0,0,0.9)', zIndex: 999999 }}
                    onClick={() => setShowFullQR(false)}
                >
                    <div className="position-relative p-16 animate-zoomIn d-flex flex-column align-items-center" style={{ zIndex: 1000000, maxWidth: '100%' }} onClick={e => e.stopPropagation()}>
                        <button 
                            className="position-absolute top-0 end-0 m-n16 bg-white w-40 h-40 rounded-circle border-0 shadow-lg flex-center"
                            onClick={() => setShowFullQR(false)}
                            style={{ zIndex: 1000001, cursor: 'pointer' }}
                        >
                            <i className="ph ph-x fw-bold text-xl"></i>
                        </button>
                        <div className="rounded-24 shadow-2xl bg-white p-24 d-inline-block">
                            <div className="position-relative d-inline-block">
                                <QRCode 
                                    value={upiString} 
                                    size={300}
                                    level="H"
                                    style={{ height: "auto", maxWidth: "90vw", maxHeight: "80vh", display: 'block' }}
                                    viewBox={`0 0 256 256`}
                                />
                                <div className="position-absolute top-50 start-50 translate-middle bg-white p-8 rounded-circle shadow-sm d-flex align-items-center justify-content-center" style={{ width: '84px', height: '84px' }}>
                                    <img src="/assets/images/logo/logo2.png" alt="Logo" className="w-100 h-100 object-fit-contain rounded-circle" />
                                </div>
                            </div>
                        <div className="mt-16 pt-16 border-top border-gray-100 text-start ps-12">
                                <p className="text-lg fw-bold text-gray-800 mb-8" style={{ wordBreak: 'break-all' }}>
                                    <span className="text-sm text-gray-500 uppercase tracking-wider me-12">UPI ID :</span> 
                                    {upiId}
                                </p>
                                <p className="text-lg fw-bold text-gray-800 mb-0">
                                    <span className="text-sm text-gray-500 uppercase tracking-wider me-12">UPI NO :</span> 
                                    {paymentSettings?.gpayNumber}
                                </p>
                            </div>
                        </div>
                        <div className="mt-24 text-center text-white">
                            <h5 className="mb-8 text-white fw-bold">Scan to Pay Rs.{finalTotal.toFixed(0)}</h5>
                            <p className="text-sm fw-medium mb-12">{merchantName}</p>
                            <p className="mb-0 opacity-50 text-xs">Tap anywhere outside to close</p>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

export default PaymentSection;